import styled from 'styled-components';
import { Link } from 'react-router-dom';

import { rem } from 'approot/shared/style';
import {
  spacing12,
  spacing16,
  spacing8,
} from 'approot/shared/styles/spacing.style';
import {
  fontWeightMedium,
  font16,
  SmallStyled,
  font12,
  fontWeightBold,
} from 'approot/shared/styles/typography.style';
import { LAYOUT_HEADER_HEIGHT } from 'approot/shared/layout/layout.styles';
import {
  NavLinkStyled,
  NavButtonStyled,
} from 'approot/shared/layout/navigation/navigation.style';
import {
  borderRadius8,
  borderRadius16,
  standardFocusState,
} from 'approot/shared/styles/borders.style';
import { HrStyled } from 'approot/shared/styles/decorative.style';
import {
  grey1,
  grey10,
  grey2,
  grey4,
  grey8,
  purple8,
  SubjectColours,
} from 'approot/shared/styles/colours.style';

export const MegaNavMenu = styled.div`
  background: ${grey1};
  box-shadow: 0px 4px 12px -4px rgba(22, 34, 51, 0.12),
    0px 16px 32px rgba(22, 34, 51, 0.16);
  border-radius: ${rem(borderRadius16)};
  border: 1px solid ${grey4};
  display: none;
  min-width: ${rem(272)};
  max-width: ${rem(746)};
  padding: ${rem(spacing12)};
  position: absolute;
  top: ${rem(LAYOUT_HEADER_HEIGHT - spacing12)};
  z-index: 100;
`;

type MegaNavProps = {
  activeSection?: string;
};
export const MegaNav = styled.nav<MegaNavProps>`
  display: flex;
  align-items: center;
  height: ${rem(LAYOUT_HEADER_HEIGHT)};

  ul,
  ul li {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  ${({ activeSection }) =>
    activeSection &&
    `
    #${activeSection} + ${MegaNavMenu} {
      display: block;
    }
  `}

  ${NavLinkStyled},
  ${NavButtonStyled} {
    z-index: 101;
  }
`;

export const MegaNavMenuGroup = styled.ul`
  display: flex;
  height: 100%;
`;

export const MegaNavButtonText = styled.span`
  display: inline-block;
  vertical-align: bottom;
`;

type MegaNavMenuItemProps = {
  $isActive: boolean;
};
export const MegaNavMenuItem = styled.li<MegaNavMenuItemProps>`
  align-items: center;
  display: flex;

  & > ${NavLinkStyled}, & > ${NavButtonStyled} {
    text-align: center;
  }

  ${NavLinkStyled}, ${MegaNavButtonText} {
    &:after {
      display: block;
      content: attr(data-title);
      font-weight: ${fontWeightMedium};
      height: 0;
      overflow: hidden;
      visibility: hidden;
    }
  }

  ${({ $isActive }) =>
    $isActive &&
    `
    & > ${NavLinkStyled},
    & > ${NavButtonStyled} {
      color: ${purple8};
      font-weight: ${fontWeightMedium};
      svg {
        stroke: ${purple8};
        stroke-width: 2;
      }
    }
  `}
`;

export const MegaNavIcon = styled.span`
  width: ${rem(56)};
  height: ${rem(56)};
  border-radius: ${rem(borderRadius8)};
  background-color: ${SubjectColours.azure[1]};
  display: flex;
  align-items: center;
  flex-shrink: 0;
  justify-content: center;
  margin-right: ${rem(spacing16)};
`;

export const MegaNavText = styled.div`
  color: ${grey10};
  ${font16}
  margin-bottom: 0;
`;

export const MegaNavSmallText = styled(SmallStyled)`
  color: ${grey8};
  margin-bottom: 0;
`;

export const MegaNavLink = styled(Link)`
  border-radius: ${rem(borderRadius8)};
  color: ${grey10};
  display: flex;
  align-items: center;
  padding: ${rem(spacing8)} ${rem(spacing12)};
  width: 100%;
  text-decoration: none;

  &:hover,
  &:focus {
    background-color: ${grey2};
    color: ${grey10};
  }

  ${standardFocusState}
`;

export const MegaNavSmallLink = styled(MegaNavLink)`
  padding: ${rem(spacing12)};
`;

export const MegaNavList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  & > li {
    flex-basis: 50%;
    max-width: ${rem(365)};
  }
`;

export const MegaNavStackedList = styled.ul`
  display: flex;
  flex-direction: column;
`;

export const MegaNavHr = styled(HrStyled)`
  margin: ${rem(spacing12)} -${rem(spacing12)};
`;

type MegaNavSubjectIconProps = {
  $colour: string;
};

export const MegaNavSubjectIcon = styled(MegaNavIcon)<MegaNavSubjectIconProps>`
  background-color: ${({ $colour }) => SubjectColours[$colour]?.[2]};
  border-radius: ${rem(borderRadius8)};
  width: ${rem(40)};
  height: ${rem(40)};
  overflow: hidden;
`;

export const MegaNavSubTitle = styled.h2`
  ${font12}
  color: ${grey10};
  font-weight: ${fontWeightBold};
  letter-spacing: 1px;
  padding: ${rem(spacing12)} ${rem(spacing12)} 0 ${rem(spacing12)};
`;

export const MegaNavColumns = styled.div`
  display: flex;
`;

export const MegaNavColumn = styled.div`
  flex: 1;
`;
